import { useEffect, useState } from 'react'

import { baseClient } from '~/clients/api-client'
import { UserInfoResponse } from '~/clients/derived-api-types'
import env from '~/env'
import { fetchAccessToken } from '~/utils/token'

export function useFetchUserInfo() {
    const [data, setData] = useState<UserInfoResponse>()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const controller = new AbortController()
        let retryTimeout: ReturnType<typeof setTimeout> | undefined

        async function fetchUserInfo() {
            try {
                // A userinfo 401 can clear refresh credentials. Renew before that request,
                // since the scheduled refresh loop needs successful userinfo to start.
                if (env.VITE_FF_REFRESH_TOKEN) {
                    const token = await fetchAccessToken()
                    if (controller.signal.aborted) return
                    const sessionExpired = token.response.status === 401 && token.error?.code === 'SESSION_EXPIRED'
                    if (!token.data) {
                        if (sessionExpired) {
                            setIsLoading(false)
                        } else {
                            retryTimeout = setTimeout(() => void fetchUserInfo(), 5000)
                        }
                        return
                    }
                }

                const userInfo = await baseClient.GET('/oauth/userinfo', { signal: controller.signal })
                if (controller.signal.aborted) return
                // A temporary outage does not establish that the session is unauthenticated.
                if (userInfo.response.status >= 500) {
                    retryTimeout = setTimeout(() => void fetchUserInfo(), 5000)
                    return
                }
                setData(userInfo.data)
                setIsLoading(false)
            } catch {
                if (!controller.signal.aborted) retryTimeout = setTimeout(() => void fetchUserInfo(), 5000)
            }
        }

        void fetchUserInfo()
        return () => {
            controller.abort()
            clearTimeout(retryTimeout)
        }
    }, [])

    return { data, isLoading }
}
